import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSwipeCarousel, type UseSwipeCarouselReturn } from '../../hooks/useSwipeCarousel'

/**
 * AGC: Defines the shape of data passed to the item renderer.
 */
interface RenderItemArgs<T> {
  /** AGC: Item currently being rendered. */
  item: T
  /** AGC: Position of the item within the carousel. */
  index: number
  /** AGC: Indicates whether the item is centred in view. */
  isActive: boolean
  /** AGC: Indicates whether the item is the externally selected entry. */
  isSelected: boolean
  /** AGC: Helper function that triggers selection of the item. */
  select: () => void
}

/**
 * AGC: Accepted sizing presets for card widths.
 */
type CarouselSize = 'large' | 'medium' | 'compact' | 'tiny' | 'tinyPlus' | 'mediumMinus' | 'fullWidth'

/**
 * AGC: Props for the generic swipe carousel component.
 */
interface SwipeCarouselProps<T> {
  /** AGC: Collection of items to display. */
  items: T[]
  /** AGC: Currently selected item index controlled by the parent. */
  selectedIndex: number
  /** AGC: Callback fired whenever a new index is selected. */
  onSelect: (index: number) => void
  /** AGC: Renderer responsible for producing the card content. */
  renderItem: (args: RenderItemArgs<T>) => React.ReactNode
  /** AGC: Visual density preset (defaults to 'medium'). */
  size?: CarouselSize
  /** AGC: Optional className for the outer container. */
  className?: string
  /** AGC: Enables cyclic navigation so the carousel never hits hard edges. */
  loop?: boolean
}

/**
 * AGC: Maps size presets to width ratios and spacing values.
 */
const SIZE_CONFIG: Record<CarouselSize, { widthRatio: number; mobileWidthRatio?: number; gap: number; mobileGap?: number; heightClass: string; maxItemWidth: number; maxItemHeight: number }> = {
  large: {
    widthRatio: 0.8,
    mobileWidthRatio: 0.58,
    gap: 28,
    mobileGap: 18,
    heightClass: 'min-h-[280px] sm:min-h-[320px]',
    maxItemWidth: 360,
    maxItemHeight: 320
  },
  medium: {
    widthRatio: 0.65,
    mobileWidthRatio: 0.6,
    gap: 24,
    mobileGap: 18,
    heightClass: 'min-h-[200px] sm:min-h-[220px]',
    maxItemWidth: 320,
    maxItemHeight: 280
  },
  compact: {
    widthRatio: 0.3,
    mobileWidthRatio: 0.35,
    gap: 12,
    mobileGap: 10,
    heightClass: 'min-h-[140px] sm:min-h-[160px]',
    maxItemWidth: 180,
    maxItemHeight: 160
  },
  tiny: {
    widthRatio: 0.195,
    mobileWidthRatio: 0.23,
    gap: 8,
    mobileGap: 6,
    heightClass: 'min-h-[80px] sm:min-h-[100px]',
    maxItemWidth: 120,
    maxItemHeight: 100
  },
  tinyPlus: {
    widthRatio: 0.2145, // AGC: 10% bigger than tiny (0.195 * 1.1)
    mobileWidthRatio: 0.253, // AGC: 10% bigger than tiny mobile (0.23 * 1.1)
    gap: 9,
    mobileGap: 7,
    heightClass: 'min-h-[88px] sm:min-h-[110px]',
    maxItemWidth: 132, // AGC: 10% bigger (120 * 1.1)
    maxItemHeight: 110 // AGC: 10% bigger (100 * 1.1)
  },
  mediumMinus: {
    widthRatio: 0.585, // AGC: 10% smaller than medium (0.65 * 0.9)
    mobileWidthRatio: 0.54, // AGC: 10% smaller than medium mobile (0.6 * 0.9)
    gap: 22,
    mobileGap: 16,
    heightClass: 'min-h-[180px] sm:min-h-[198px]',
    maxItemWidth: 288, // AGC: 10% smaller (320 * 0.9)
    maxItemHeight: 252 // AGC: 10% smaller (280 * 0.9)
  },
  fullWidth: {
    widthRatio: 1.0, // AGC: Full width - 100% of container
    mobileWidthRatio: 1.0, // AGC: Full width on mobile too
    gap: 0, // AGC: No gap between cards (full screen)
    mobileGap: 0,
    heightClass: 'min-h-[400px] sm:min-h-[450px]',
    maxItemWidth: 9999, // AGC: No max width limit
    maxItemHeight: 9999 // AGC: No max height limit
  }
}

interface CarouselTrackProps<T> {
  /** AGC: Collection of base carousel items. */
  items: T[]
  /** AGC: Fixed width computed for each card. */
  renderedWidth: number
  /** AGC: Gap between cards in pixels. */
  gap: number
  /** AGC: Whether the carousel is actively dragging. */
  isDragging: boolean
  /** AGC: Precomputed translate offset for the track. */
  translateX: number
  /** AGC: Currently focused item index. */
  currentIndex: number
  /** AGC: Externally selected item index. */
  selectedIndex: number
  /** AGC: Setter provided by swipe hook. */
  navigateToIndex: (index: number, options?: { triggerCallback?: boolean }) => void
  /** AGC: Callback to notify parent when a new item is chosen. */
  onSelect: (index: number) => void
  /** AGC: Renderer from parent component. */
  renderItem: (args: RenderItemArgs<T>) => React.ReactNode
  /** AGC: Indicates if we should render head/tail clones for looping previews. */
  hasLoopClones: boolean
}

const CarouselTrack = <T,>({
  items,
  renderedWidth,
  gap,
  isDragging,
  translateX,
  currentIndex,
  selectedIndex,
  navigateToIndex,
  onSelect,
  renderItem,
  hasLoopClones
}: CarouselTrackProps<T>) => {
  const renderEntries = useMemo(() => {
    const base = items.map((item, index) => ({ item, actualIndex: index, key: `item-${index}` }))
    if (!hasLoopClones || items.length <= 1) {
      return base
    }

    const headClones = items.map((item, index) => ({ item, actualIndex: index, key: `clone-head-${index}` }))
    const tailClones = items.map((item, index) => ({ item, actualIndex: index, key: `clone-tail-${index}` }))

    return [...tailClones, ...base, ...headClones]
  }, [items, hasLoopClones])

  const leadingCloneCount = hasLoopClones ? items.length : 0

  return (
    <div
      className={`flex h-full items-center transition-transform ease-out ${
        isDragging ? 'duration-0' : 'duration-300'
      }`}
      style={{ transform: `translate3d(${translateX}px, 0, 0)` }}
    >
      {renderEntries.map((entry, mapIndex) => {
        const { item, actualIndex, key } = entry
        const isActive = actualIndex === currentIndex
        const isSelected = actualIndex === selectedIndex
        const cardWidth = renderedWidth || 1
        const isRealSlot = !hasLoopClones || (mapIndex >= leadingCloneCount && mapIndex < leadingCloneCount + items.length)
        const domId = isRealSlot ? `carousel-item-${actualIndex}` : `carousel-item-${actualIndex}-clone-${mapIndex}`
        const style: React.CSSProperties = {
          width: cardWidth,
          flex: '0 0 auto',
          marginRight: mapIndex === renderEntries.length - 1 ? 0 : gap,
          transform: isActive ? 'scale(1)' : 'scale(0.9)',
          transition: isDragging ? 'transform 0.1s linear' : 'transform 0.25s ease'
        }

        return (
          <div
            key={key}
            id={domId}
            role="option"
            aria-selected={isSelected}
            className="flex h-full items-center justify-center"
            style={style}
          >
            {renderItem({
              item,
              index: actualIndex,
              isActive,
              isSelected,
              select: () => {
                if (actualIndex === currentIndex) {
                  onSelect(actualIndex)
                  return
                }
                navigateToIndex(actualIndex, { triggerCallback: false })
                onSelect(actualIndex)
              }
            })}
          </div>
        )
      })}
    </div>
  )
}

/**
 * AGC: Swipeable carousel that centres one item and peeks the neighbours.
 */
export const SwipeCarousel = <T,>({
  items,
  selectedIndex,
  onSelect,
  renderItem,
  size = 'medium',
  className,
  loop = true
}: SwipeCarouselProps<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  const { widthRatio, mobileWidthRatio, gap, mobileGap, heightClass, maxItemWidth } = SIZE_CONFIG[size]

  /** AGC: Hook managing low-level swipe gestures. */
  const swipeAPI: UseSwipeCarouselReturn = useSwipeCarousel({
    length: items.length,
    initialIndex: selectedIndex,
    controlledIndex: selectedIndex,
    loop,
    onIndexChange: onSelect
  })

  const {
    currentIndex,
    virtualIndex,
    setIndex,
    isDragging,
    dragOffset,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleKeyDown
  } = swipeAPI

  /**
   * AGC: Observe container resize to compute pixel-based transforms.
   */
  useEffect(() => {
    if (!containerRef.current) {
      return
    }

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }

    updateWidth()

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateWidth)
      observer.observe(containerRef.current)
      return () => observer.disconnect()
    }

    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  /** AGC: Derived dimensions for items and track based on current width. */
  const { itemWidth, effectiveGap, trackOffset } = useMemo(() => {
    if (containerWidth === 0) {
      return { itemWidth: 0, effectiveGap: gap, trackOffset: 0 }
    }
    const ratio = containerWidth <= 640 && mobileWidthRatio ? mobileWidthRatio : widthRatio
    const computedItemWidth = Math.min(containerWidth * ratio, maxItemWidth)
    const gapValue = containerWidth <= 640 && typeof mobileGap === 'number' ? mobileGap : gap
    const centreOffset = (containerWidth - computedItemWidth) / 2
    return { itemWidth: computedItemWidth, effectiveGap: gapValue, trackOffset: centreOffset }
  }, [containerWidth, widthRatio, mobileWidthRatio, gap, mobileGap, maxItemWidth])

  const hasLoopClones = loop && items.length > 1

  /** AGC: Compose transform for the inner track based on index and drag offset. */
  const translateX = useMemo(() => {
    if (items.length === 0) {
      return 0
    }
    const leadingCloneCount = hasLoopClones ? items.length : 0
    const effectiveIndex = hasLoopClones ? virtualIndex : currentIndex
    const base = -(effectiveIndex + leadingCloneCount) * (itemWidth + effectiveGap)
    return base + trackOffset + dragOffset
  }, [effectiveGap, itemWidth, items.length, currentIndex, virtualIndex, dragOffset, trackOffset, hasLoopClones])

  const moveToIndex = useCallback(
    (target: number, options?: { triggerCallback?: boolean }) => {
      if (!loop || items.length === 0) {
        setIndex(target, options)
        return
      }

      const length = items.length
      const currentVirtual = virtualIndex
      const currentDisplay = ((currentVirtual % length) + length) % length
      let targetVirtual = currentVirtual + (target - currentDisplay)

      if (targetVirtual - currentVirtual > length / 2) {
        targetVirtual -= length
      } else if (currentVirtual - targetVirtual > length / 2) {
        targetVirtual += length
      }

      setIndex(targetVirtual, options)
    },
    [items.length, loop, setIndex, virtualIndex]
  )

  return (
    <div
      ref={containerRef}
      className={[
        'relative w-full select-none',
        heightClass,
        className ?? ''
      ]
        .filter(Boolean)
        .join(' ')}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      role="listbox"
      aria-activedescendant={`carousel-item-${currentIndex}`}
      aria-live="polite"
    >
      <div className="absolute inset-0 overflow-hidden">
        <CarouselTrack
          items={items}
          renderedWidth={itemWidth}
          gap={effectiveGap}
          isDragging={isDragging}
          translateX={translateX}
          currentIndex={currentIndex}
          selectedIndex={selectedIndex}
          navigateToIndex={moveToIndex}
          onSelect={onSelect}
          renderItem={renderItem}
          hasLoopClones={hasLoopClones}
        />
      </div>

      {items.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
          {items.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-label={`Go to item ${index + 1}`}
              className={`h-3 w-3 rounded-full transition-all sm:h-2 sm:w-2 ${
                index === currentIndex ? 'bg-white scale-125' : 'bg-white/40 hover:bg-white/70'
              }`}
              onClick={() => {
                if (index === currentIndex) {
                  onSelect(index)
                  return
                }
                moveToIndex(index, { triggerCallback: false })
                onSelect(index)
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}


